import React, { useState, useEffect, useCallback } from 'react'
import {
  RefreshCwIcon,
  Loader2Icon,
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayIcon,
  ClockIcon,
  CalendarIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  MobilePage,
  MobileListRow,
  MobileEmptyState,
  mobile,
} from '../components/mobile'

interface RunDetail {
  contract_id: string
  jobs_created: number
  jobs_skipped: number
  error_message: string | null
}

interface RunLog {
  id: string
  run_type: string
  started_at: string
  finished_at: string | null
  contracts_processed: number
  total_jobs_created: number
  total_jobs_skipped: number
  total_errors: number
  details: RunDetail[]
  triggered_by: string | null
  created_at: string
}

interface ContractInfo {
  id: string
  title: string | null
  customer_name: string | null
}

export function SystemLogs() {
  const { user, session } = useAuth()
  const [logs, setLogs] = useState<RunLog[]>([])
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [contractMap, setContractMap] = useState<Record<string, ContractInfo>>({})
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('recurring_job_run_logs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50)

      if (error) throw error
      setLogs(data || [])

      const contractIds = new Set<string>()
      ;(data || []).forEach((log: RunLog) => {
        ;(log.details || []).forEach((d: RunDetail) => {
          if (d.contract_id) contractIds.add(d.contract_id)
        })
      })

      if (contractIds.size > 0) {
        const { data: contracts } = await supabase
          .from('contracts')
          .select('id, title, customer:customers(name)')
          .in('id', Array.from(contractIds))

        if (contracts) {
          const map: Record<string, ContractInfo> = {}
          contracts.forEach((c: any) => {
            map[c.id] = {
              id: c.id,
              title: c.title,
              customer_name: c.customer?.name || null,
            }
          })
          setContractMap(map)
        }
      }
    } catch (err: any) {
      console.error('Error fetching run logs:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  const handleManualRun = async () => {
    if (running) return
    setShowConfirmModal(false)
    setRunning(true)
    setMessage(null)

    try {
      const token = session?.access_token
      if (!token) throw new Error('Not signed in')

      const res = await fetch('/api/generate-nightly-jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok || body.success === false) {
        throw new Error(body.message || body.error || 'Failed to generate jobs')
      }

      const runId = body.result?.runId || body.result?.runs?.[0]?.runId || 'done'
      setMessage({
        type: 'success',
        text: `Recurring job generation completed successfully! Run ID: ${runId}`,
      })
      await fetchLogs()
    } catch (err: any) {
      console.error('Error running recurring jobs:', err)
      setMessage({
        type: 'error',
        text: 'Failed to run recurring job generation: ' + (err.message || 'Unknown error'),
      })
    } finally {
      setRunning(false)
      setTimeout(() => setMessage(null), 8000)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
  }

  const getDuration = (start: string, end: string | null) => {
    if (!end) return 'Running...'
    const ms = new Date(end).getTime() - new Date(start).getTime()
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
  }

  const getRunTypeBadge = (runType: string) => {
    switch (runType) {
      case 'nightly':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
            <ClockIcon className="w-3 h-3" /> Nightly
          </span>
        )
      case 'manual':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <PlayIcon className="w-3 h-3" /> Manual
          </span>
        )
      case 'activation':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircleIcon className="w-3 h-3" /> Activation
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {runType}
          </span>
        )
    }
  }

  const getStatusIcon = (log: RunLog) => {
    if (!log.finished_at) {
      return <Loader2Icon className="w-5 h-5 text-blue-500 animate-spin" />
    }
    if (log.total_errors > 0) {
      return <AlertTriangleIcon className="w-5 h-5 text-amber-500" />
    }
    if (log.total_jobs_created === 0 && log.contracts_processed > 0) {
      return <CheckCircleIcon className="w-5 h-5 text-gray-400" />
    }
    return <CheckCircleIcon className="w-5 h-5 text-green-500" />
  }

  return (
    <MobilePage>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Logs</h1>
          <p className="mt-1 text-gray-600">
            View recurring job generation history and run manual top-ups
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowConfirmModal(true)}
            disabled={running}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {running ? (
              <>
                <Loader2Icon className="w-4 h-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                Run Recurring Top-Up
              </>
            )}
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Summary Cards */}
      {logs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-500">Total Runs</p>
            <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-500">Last Run</p>
            <p className="text-sm font-semibold text-gray-900">{formatDate(logs[0]?.started_at)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-500">Jobs Created (Last Run)</p>
            <p className="text-2xl font-bold text-green-600">{logs[0]?.total_jobs_created || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-500">Errors (Last Run)</p>
            <p className={`text-2xl font-bold ${(logs[0]?.total_errors || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {logs[0]?.total_errors || 0}
            </p>
          </div>
        </div>
      )}

      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : logs.length === 0 ? (
          <MobileEmptyState
            icon={<CalendarIcon className="w-12 h-12" />}
            title="No run history yet"
            description="Run your first recurring top-up to see results here."
            action={
              <button type="button" onClick={() => setShowConfirmModal(true)} disabled={running} className={mobile.primaryBtn}>
                <PlayIcon className="w-4 h-4" />
                Run Top-Up
              </button>
            }
          />
        ) : (
          logs.map((log) => (
            <MobileListRow
              key={log.id}
              title={formatDate(log.started_at)}
              subtitle={`${log.total_jobs_created} created · ${log.total_jobs_skipped} skipped · ${log.total_errors} errors`}
              badges={getRunTypeBadge(log.run_type)}
              meta={<div>Duration {getDuration(log.started_at, log.finished_at)}</div>}
              chevron={false}
            />
          ))
        )}
      </div>

      {/* Logs Table */}
      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16">
            <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No run history yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Run your first recurring top-up to see results here.
            </p>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={running}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlayIcon className="w-4 h-4" />
              Run Recurring Top-Up
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date / Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contracts
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Skipped
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Errors
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Triggered By
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusIcon(log)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getRunTypeBadge(log.run_type)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.started_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {getDuration(log.started_at, log.finished_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                        {log.contracts_processed}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-green-600 text-right">
                        {log.total_jobs_created}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                        {log.total_jobs_skipped}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                        {log.total_errors > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-medium">
                            <XCircleIcon className="w-4 h-4" />
                            {log.total_errors}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {log.triggered_by || 'System (Cron)'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {(log.details || []).length > 0 && (
                          <button
                            onClick={() =>
                              setExpandedRow(expandedRow === log.id ? null : log.id)
                            }
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {expandedRow === log.id ? (
                              <>
                                Hide <ChevronUpIcon className="w-4 h-4" />
                              </>
                            ) : (
                              <>
                                View <ChevronDownIcon className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedRow === log.id && (
                      <tr>
                        <td colSpan={10} className="px-4 py-0">
                          <div className="py-3 px-6 bg-gray-50 rounded-lg my-2">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Per-Contract Breakdown
                            </h4>
                            <table className="min-w-full text-sm">
                              <thead>
                                <tr className="text-xs text-gray-500 uppercase">
                                  <th className="text-left py-1 pr-4">Contract</th>
                                  <th className="text-right py-1 px-4">Created</th>
                                  <th className="text-right py-1 px-4">Skipped</th>
                                  <th className="text-left py-1 pl-4">Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(log.details || []).map((detail: RunDetail, idx: number) => {
                                  const info = contractMap[detail.contract_id]
                                  return (
                                    <tr key={idx} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-4 text-gray-900">
                                        {info ? (
                                          <span>
                                            {info.title || 'Untitled'}{' '}
                                            {info.customer_name && (
                                              <span className="text-gray-500">
                                                — {info.customer_name}
                                              </span>
                                            )}
                                          </span>
                                        ) : (
                                          <span className="text-gray-400 font-mono text-xs">
                                            {detail.contract_id.slice(0, 8)}...
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-1.5 px-4 text-right font-medium text-green-600">
                                        {detail.jobs_created}
                                      </td>
                                      <td className="py-1.5 px-4 text-right text-gray-500">
                                        {detail.jobs_skipped}
                                      </td>
                                      <td className="py-1.5 pl-4 text-red-600">
                                        {detail.error_message || '—'}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Run Recurring Top-Up
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              This will generate recurring jobs for all active contracts for the next 90 days. Jobs that already exist will be skipped.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleManualRun}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Run Top-Up
              </button>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
